import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Feather,
  Check,
  Loader2,
  ShieldCheck,
  ArrowLeft,
  CreditCard,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

const FREE_FEATURES = [
  "5 AI rewrites every day",
  "All 6 writing tools",
  "Personal rewrite history",
  "Beginner-friendly UI",
];

const PRO_FEATURES = [
  "Unlimited AI rewrites",
  "Priority processing",
  "All 6 writing tools",
  "Cancel anytime",
];

const PLAN_META = {
  monthly: {
    label: "Monthly",
    price: 9,
    per: "/month",
    subline: "Billed monthly · cancel anytime",
    fullYear: 108,
  },
  yearly: {
    label: "Yearly",
    price: 79,
    per: "/year",
    subline: "That's $6.58/month — 2 months free",
    fullYear: 79,
    badge: "Save $29",
  },
};

function BillingToggle({ value, onChange }) {
  return (
    <div
      data-testid="billing-toggle"
      className="inline-flex items-center rounded-full border border-white/15 bg-white/5 p-1 text-xs font-semibold"
    >
      <button
        data-testid="billing-toggle-monthly"
        onClick={() => onChange("monthly")}
        className={`rounded-full px-3 py-1.5 transition-colors ${
          value === "monthly"
            ? "bg-white text-[#1A1D20]"
            : "text-white/60 hover:text-white"
        }`}
      >
        Monthly
      </button>
      <button
        data-testid="billing-toggle-yearly"
        onClick={() => onChange("yearly")}
        className={`relative rounded-full px-3 py-1.5 transition-colors ${
          value === "yearly"
            ? "bg-white text-[#1A1D20]"
            : "text-white/60 hover:text-white"
        }`}
      >
        Yearly
        <span className="ml-1.5 inline-flex items-center rounded-full bg-emerald-400 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-emerald-900">
          2 mo free
        </span>
      </button>
    </div>
  );
}

function StripeCheckoutButton({ plan }) {
  const [busy, setBusy] = useState(false);
  const meta = PLAN_META[plan];
  const start = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/stripe/checkout", {
        origin_url: window.location.origin,
        plan,
      });
      if (data?.checkout_url) {
        window.location.href = data.checkout_url;
        return;
      }
      toast.error("Could not start checkout");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Button
      data-testid={`stripe-checkout-button-${plan}`}
      onClick={start}
      disabled={busy}
      className="group h-12 w-full gap-2 rounded-full bg-white text-sm font-semibold text-[#1A1D20] shadow-[0_8px_24px_-8px_rgba(0,0,0,0.3)] hover:bg-slate-50 active:scale-[0.99]"
    >
      {busy ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : (
        <>
          <CreditCard className="h-4 w-4" />
          Subscribe · ${meta.price}
          {meta.per}
        </>
      )}
    </Button>
  );
}

export default function Pricing() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [plan, setPlan] = useState("yearly");
  const [plans, setPlans] = useState(null);

  useEffect(() => {
    api
      .get("/stripe/config")
      .then((r) => setPlans(r.data?.plans || []))
      .catch(() => {});
  }, []);

  const meta = PLAN_META[plan];

  const cancel = async () => {
    if (!window.confirm("Cancel your Pro subscription?")) return;
    try {
      const { data } = await api.post("/stripe/cancel");
      setUser(data.user);
      toast.success("Subscription cancelled");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not cancel");
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF9F5]">
      <header className="border-b border-[#E6E2D8]/60 bg-[#FAF9F5]/80 backdrop-blur-sm">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
          <Link to="/app" className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#1A1D20] text-[#FAF9F5]">
              <Feather className="h-4 w-4" />
            </div>
            <div>
              <h1 className="font-heading text-lg font-bold leading-none text-[#1A1D20]">
                Write It Easy AI
              </h1>
              <p className="mt-1 text-[11px] font-medium uppercase tracking-widest text-slate-500">
                Pricing
              </p>
            </div>
          </Link>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/app")}
            className="gap-2 text-slate-600"
            data-testid="back-to-app"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to editor
          </Button>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-4 py-10 sm:px-6 lg:px-8 lg:py-16">
        <div className="text-center">
          <p className="mb-2 text-xs font-semibold uppercase tracking-[0.18em] text-indigo-700">
            Simple pricing
          </p>
          <h2 className="font-heading text-4xl font-extrabold tracking-tight text-[#1A1D20] sm:text-5xl">
            Free to start.{" "}
            <span className="text-indigo-700">Pro when you need it.</span>
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-base text-slate-600">
            Every plan includes all 6 writing tools. Upgrade any time — cancel
            any time.
          </p>
        </div>

        <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-2">
          {/* Free */}
          <div
            data-testid="pricing-free-card"
            className="rounded-3xl border border-[#E6E2D8] bg-white p-8 shadow-[0_8px_30px_-12px_rgba(0,0,0,0.06)]"
          >
            <div className="flex items-center justify-between">
              <h3 className="font-heading text-xl font-bold text-[#1A1D20]">
                Free
              </h3>
              <Badge
                variant="secondary"
                className="rounded-full bg-slate-100 text-slate-700"
              >
                Current default
              </Badge>
            </div>
            <div className="mt-4 flex items-baseline gap-1">
              <span className="font-heading text-5xl font-extrabold text-[#1A1D20]">
                $0
              </span>
              <span className="text-slate-500">/month</span>
            </div>
            <p className="mt-2 text-sm text-slate-500">
              Perfect for occasional writing help.
            </p>
            <ul className="mt-6 space-y-3">
              {FREE_FEATURES.map((f) => (
                <li
                  key={f}
                  className="flex items-start gap-2 text-sm text-slate-700"
                >
                  <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-600" />
                  {f}
                </li>
              ))}
            </ul>
            <Button
              disabled
              variant="outline"
              className="mt-8 h-11 w-full rounded-full border-[#E6E2D8] bg-transparent text-slate-500"
            >
              {user?.is_pro ? "Downgrade requires cancel" : "You're on Free"}
            </Button>
          </div>

          {/* Pro */}
          <div
            data-testid="pricing-pro-card"
            className="relative rounded-3xl border-2 border-[#1A1D20] bg-[#1A1D20] p-8 text-white shadow-[0_20px_60px_-20px_rgba(26,29,32,0.5)]"
          >
            <div className="absolute -top-3 left-8">
              <span className="rounded-full bg-amber-400 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-[#1A1D20]">
                Most popular
              </span>
            </div>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="flex items-center gap-2">
                <h3 className="font-heading text-xl font-bold">Pro</h3>
                <ShieldCheck className="h-5 w-5 text-emerald-400" />
              </div>
              {!user?.is_pro && (
                <BillingToggle value={plan} onChange={setPlan} />
              )}
            </div>
            <div className="mt-4 flex items-baseline gap-1">
              <span
                data-testid="pro-price"
                className="font-heading text-5xl font-extrabold"
              >
                ${meta.price}
              </span>
              <span className="text-white/60">{meta.per}</span>
              {plan === "yearly" && !user?.is_pro && (
                <span
                  data-testid="yearly-savings-pill"
                  className="ml-2 inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-emerald-300"
                >
                  <Sparkles className="h-3 w-3" />
                  {meta.badge}
                </span>
              )}
            </div>
            <p className="mt-2 text-sm text-white/70">{meta.subline}</p>
            <ul className="mt-6 space-y-3">
              {PRO_FEATURES.map((f) => (
                <li
                  key={f}
                  className="flex items-start gap-2 text-sm text-white/90"
                >
                  <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-400" />
                  {f}
                </li>
              ))}
            </ul>

            <div className="mt-8">
              {user?.is_pro ? (
                <div className="space-y-3">
                  <div
                    data-testid="pro-active-panel"
                    className="rounded-xl border border-emerald-400/30 bg-emerald-500/10 p-3 text-sm text-emerald-200"
                  >
                    You're on Pro
                    {user.pro_until && (
                      <span className="ml-1 text-white/60">
                        · renews{" "}
                        {new Date(user.pro_until).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                  <Button
                    onClick={cancel}
                    variant="outline"
                    data-testid="cancel-subscription-button"
                    className="h-10 w-full rounded-full border-white/30 bg-transparent text-sm text-white hover:bg-white/10"
                  >
                    Cancel subscription
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  <div
                    data-testid="stripe-managed-banner"
                    className="flex items-start gap-2 rounded-xl border border-emerald-400/30 bg-emerald-500/10 p-3 text-xs text-emerald-100"
                  >
                    <ShieldCheck className="mt-0.5 h-4 w-4 flex-shrink-0" />
                    <div>
                      Paid via <span className="font-semibold">Stripe</span> —
                      no account needed. Test with card{" "}
                      <code>4242 4242 4242 4242</code>, any future expiry, any
                      CVC.
                    </div>
                  </div>
                  <StripeCheckoutButton plan={plan} />
                  {plan === "yearly" && (
                    <p className="text-center text-[11px] text-white/50">
                      $79 billed today · saves $29 vs monthly
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        <p className="mt-8 text-center text-xs text-slate-400">
          Secure payments through Stripe · Cancel anytime · No hidden fees
        </p>
      </main>
    </div>
  );
}
